import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dialog, withStyles } from "@material-ui/core";
import BinaryImageForm from './BinaryImageForm';
import * as Yup from 'yup';
import * as actions from "./store/binaryImageActions";
import History from '../../../History';
import { hasPermission, parseURL, isValidHex, hexToDec, toHex } from '../../sc-utilities';
import { CancelToken } from 'axios';
import AppLogoLoading from '../../sc-logo';

const styles = theme => ({
    dialog: {
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "hidden",
        alignItems: "center"
    },
    logo: {
        width: "250px",
        margin: "10px"
    }
});

const BinaryImageEdit = props => {
    const { classes, loading, activeCustomerID, translations, permissions, editBinaryImage, onLoadBinaryImage, onFetchBinaryImageTypes, onClearState, binaryImage, binaryImageType, customers } = props;
    let searchParams = parseURL(window.location.search);
    const [fetchLoading, setFetchLoading] = useState(false);

    const handleSubmit = async dataForm => {
        const data = new FormData();
        data.append("BinaryImageID", searchParams.binaryImageID);
        data.append("MajorVersion", dataForm.MajorVersion);
        data.append("MinorVersion", dataForm.MinorVersion);
        data.append("BuildNumber", dataForm.BuildNumber);
        data.append("BinaryImageTypeID", dataForm.BinaryImageTypeID);
        data.append("Name", dataForm.Name);
        data.append("BuildType", dataForm.BuildType);
        data.append("Public", dataForm.Public === "yes" ? true : false);
        data.append("Severity", dataForm.Severity);
        data.append("ReleaseNotes", dataForm.ReleaseNotes);
        data.append("ActiveCustomerID", activeCustomerID);
        data.append("Type", dataForm.Type);
        data.append("URL", dataForm.Type === 'Internal' ? null : dataForm.URL);
        if (dataForm?.Type?.includes('External')) {
            data.append("CRC", hexToDec(dataForm.CRC));
        }
        if (dataForm.Public === "no") {
            data.append("CustomerID", dataForm.CustomerID);
        } let token = localStorage.getItem("SESSION");

        data.append("Token", token);
        let result = await editBinaryImage(data);
        if (result) {
            History.goBack();
        }
    }

    const isValid = data => {
        return (data !== null && data !== undefined)
    };

    useEffect(() => {
        const requestToken = CancelToken.source();
        const fetchBinaryImageTypes = async () => {
            await onFetchBinaryImageTypes(activeCustomerID, { CancelToken: requestToken.token });
            await onLoadBinaryImage({ BinaryImageID: searchParams.binaryImageID, ActiveCustomerID: activeCustomerID }, { CancelToken: requestToken.token });
            setFetchLoading(true);
        }
        fetchBinaryImageTypes()
        return () => {
            onClearState()
            setFetchLoading(false);
            requestToken.cancel('Operation canceled by the user.');
        };

    }, [onFetchBinaryImageTypes, activeCustomerID, onLoadBinaryImage, onClearState, searchParams.binaryImageID])

    let initialValues = {
        MajorVersion: binaryImage && isValid(binaryImage.MajorVersion) ? binaryImage.MajorVersion : '',
        MinorVersion: binaryImage && isValid(binaryImage.MinorVersion) ? binaryImage.MinorVersion : '',
        BuildNumber: binaryImage && isValid(binaryImage.BuildNumber) ? binaryImage.BuildNumber : '',
        BinaryImageTypeID: binaryImage && binaryImage.BinaryImageTypeID ? binaryImage.BinaryImageTypeID : '',
        Name: binaryImage && binaryImage.Name ? binaryImage.Name : '',
        BuildType: binaryImage && binaryImage.BuildType ? binaryImage.BuildType : '',
        Public: binaryImage && binaryImage.Public ? 'yes' : 'no',
        Severity: binaryImage && binaryImage.Severity ? String(binaryImage && binaryImage.Severity) : "0",
        ReleaseNotes: binaryImage && binaryImage.ReleaseNotes ? binaryImage.ReleaseNotes : '',
        Type: binaryImage?.Type ?? 'Internal',
        URL: binaryImage?.URL ?? '',
        CRC: isValidHex(toHex(binaryImage?.CRC)) ? toHex(binaryImage?.CRC) : '',
        CustomerID: binaryImage?.CustomerID ?? ''

    };
    let formSettings = {
        Validation: {
            MajorVersion: Yup.number().required("Required"),
            MinorVersion: Yup.number().required("Required"),
            BuildNumber: Yup.number().required("Required"),
            BinaryImageTypeID: Yup.string().required('Required'),
            Name: Yup.string().required("Required"),
            Type: Yup.string().required('Required'),
            URL: Yup.string().when("Type", {
                is: val => val.includes('External'),
                then: Yup.string().required('Required')
            }),
            CRC: Yup.string().when("Type", {
                is: val => val.includes('External'),
                then: Yup.string().required('Required').test('hex', 'Is not a valid hexadecimal number.', crc => isValidHex(crc))
            }),
            CustomerID: Yup.string().when("Public", {
                is: val => (val === 'no'),
                then: Yup.string().required('Required')
            })
        },
        FormTitle: translations["edit_binary_image"] || "Edit download",
        ButtonSubmit: hasPermission(permissions, 'binary_image_save') ? {
            Title: translations["save"] || "SAVE"
        } : null
    };

    return (
        <div>
            {fetchLoading ?
                <BinaryImageForm
                    edit={true}
                    binaryImageType={binaryImageType}
                    formSettings={formSettings}
                    initialValues={initialValues}
                    handleSubmit={handleSubmit}
                    loading={loading}
                    translations={translations}
                    customers={customers}
                />
                : <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={!fetchLoading}
                    PaperProps={{
                        classes: {
                            root: classes.dialog
                        }
                    }}
                >
                    <AppLogoLoading />
                </Dialog>
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        loading: state.exportImport.binaryImages.editing,
        binaryImage: state.exportImport.binaryImages && state.exportImport.binaryImages.loaded ? state.exportImport.binaryImages.loaded : {},
        binaryImageType: state.exportImport.binaryImages ? state.exportImport.binaryImages.binaryImageTypes : {},
        customers: state?.core?.login?.customers || []
    };
};
const mapDispatchToProps = dispatch => {
    return {
        onFetchBinaryImageTypes: (activeCustomerID, options) => dispatch(actions.listBinaryImageTypes(activeCustomerID, options)),
        onLoadBinaryImage: (filter, options) => dispatch(actions.loadBinaryImage(filter, options)),
        editBinaryImage: data => dispatch(actions.editBinaryImage(data)),
        onClearState: () => dispatch(actions.ClearStateCreate()),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(BinaryImageEdit));